// =============================================================================
// Email image upload helper
// -----------------------------------------------------------------------------
// Wraps the `email-upload-image` Edge Function. Accepts a File/Blob, returns
// a public URL ready to drop into an <img src="..."> tag inside the email body.
// =============================================================================

const ALLOWED_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
]);

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB — must match the Edge Function

export function isSupportedImage(file) {
  return Boolean(file) && ALLOWED_TYPES.has(file.type);
}

/**
 * Uploads an image to the `assets` bucket via the admin-gated
 * `email-upload-image` Edge Function. Returns the raw public URL — assets are
 * uploaded as-is (no resize, no crop, no re-encode) since they are designed
 * at the dimensions we want for the email.
 *
 * @param {File|Blob} file
 * @returns {Promise<{ url: string, path: string }>}
 */
export async function uploadEmailImage(file) {
  if (!file) throw new Error('No file provided');
  if (!ALLOWED_TYPES.has(file.type)) {
    throw new Error(`Unsupported image type: ${file.type || 'unknown'}`);
  }
  if (file.size > MAX_BYTES) {
    throw new Error(
      `Image too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 8 MB.`,
    );
  }

  const { supabase } = await import('@/lib/customSupabaseClient');

  const form = new FormData();
  // Pasted screenshots from clipboard often come as Blobs without a filename;
  // give them a stable name so the Edge Function can derive an extension.
  const name = (file.name && file.name !== 'blob') ? file.name : `pasted-${Date.now()}.png`;
  form.append('file', file, name);

  const { data, error } = await supabase.functions.invoke('email-upload-image', {
    body: form,
  });

  if (error) {
    const msg = error?.message || error?.context?.body || JSON.stringify(error);
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  if (!data?.url) throw new Error('Upload returned no URL');

  // Defensive: if a previous version of the Edge Function is still deployed
  // and returns the image-transformation URL (/storage/v1/render/image/public/...),
  // normalize it to the raw object URL so the asset goes into the email at
  // its original dimensions, not a 1200px-wide re-encode.
  const url = toRawObjectUrl(data.url);

  return { url, path: data.path };
}

function toRawObjectUrl(maybeTransformUrl) {
  if (!maybeTransformUrl) return maybeTransformUrl;
  // Match: <project>/storage/v1/render/image/public/<rest>?...
  // Convert to: <project>/storage/v1/object/public/<rest>
  const match = maybeTransformUrl.match(
    /^(https?:\/\/[^/]+)\/storage\/v1\/render\/image\/public\/(.+?)(\?.*)?$/,
  );
  if (!match) return maybeTransformUrl;
  return `${match[1]}/storage/v1/object/public/${match[2]}`;
}
